import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { dayKeyOf } from "../lib/days";
import { shortDate } from "../lib/weekLine";
import { describeOutcome, summarise } from "../lib/assign";
import { tellTheClient } from "../lib/assignPush";

/** Assign from the library row: the WORKOUT is known, so the window asks WHO and WHICH DAY. It writes through the one
 *  RPC and the one outcome reader the client page's Assign window uses (`assign_coach_workout`, `describeOutcome`) —
 *  a duplicate day is refused by the database's own index and said in words. */
export function AssignFromLibraryWindow({ workout, clients, today, onClose, onAssigned }: {
  workout: { id: string; name: string }; clients: readonly { id: string; name: string }[]; today: Date;
  onClose: () => void; onAssigned: (said: string) => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [clientId, setClientId] = useState(clients[0]?.id ?? "");
  const [day, setDay] = useState(dayKeyOf(today));
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => { const el = dialogRef.current; if (el !== null && !el.open) el.showModal(); }, []);

  async function assign() {
    if (busy || clientId === "" || day === "") return;
    setBusy(true);
    const r = await supabase.rpc("assign_coach_workout", {
      p_workout_id: workout.id, p_client_id: clientId, p_scheduled_date: day, p_note: "",
    });
    setBusy(false);
    const outcome = describeOutcome(day, r.error);
    const who = clients.find((c) => c.id === clientId)?.name ?? "the client";
    if (outcome.kind === "assigned") {
      tellTheClient(r.data ?? null, 1);
      onAssigned(`${workout.name} assigned to ${who} on ${shortDate(day)}.`);
      return;
    }
    setSaid(outcome.kind === "alreadyAssigned" ? `${who} already has ${workout.name} on ${shortDate(day)}.` : summarise([outcome]));
  }

  return (
    <dialog ref={dialogRef} className="ht-window library-assign" aria-labelledby="library-assign-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="ht-window-frame library-assign-frame">
        <header className="ht-window-head">
          <div className="ht-window-titles">
            <p className="page-eyebrow">{workout.name}</p>
            <h2 className="ht-title" id="library-assign-title">Assign</h2>
          </div>
          <button type="button" className="ht-button ht-button-quiet ht-window-close" onClick={onClose}>Close</button>
        </header>
        <div className="ht-window-body library-assign-body">
          <label className="library-assign-field">
            <span className="ht-caption">Client</span>
            <select className="ht-input" value={clientId} onChange={(e) => { setClientId(e.target.value); setSaid(null); }}>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="library-assign-field">
            <span className="ht-caption">Day</span>
            <input className="ht-input" type="date" value={day} onChange={(e) => { setDay(e.target.value); setSaid(null); }} />
          </label>
          {said !== null && <p className="ht-callout" role="alert">{said}</p>}
          <button type="button" className="ht-button ht-button-primary" disabled={busy || clientId === ""}
                  onClick={() => { void assign(); }}>{busy ? "Assigning…" : "Assign"}</button>
        </div>
      </div>
    </dialog>
  );
}
